import { useEffect, useRef, useCallback } from "react";

/**
 * Subtle ambient star field - peaceful depth without dramatic light rays.
 */
export function AmbientField() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animRef = useRef<number>(0);

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const w = canvas.clientWidth;
    const h = canvas.clientHeight;
    if (canvas.width !== w * dpr || canvas.height !== h * dpr) {
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      ctx.scale(dpr, dpr);
    }

    const t = Date.now() * 0.001;
    ctx.clearRect(0, 0, w, h);

    // 80 ambient stars - subtle, low opacity
    for (let i = 0; i < 80; i++) {
      const seed = i * 137.508;
      const sx = (seed * 7.3 + 13) % w;
      const sy = (seed * 3.7 + 7) % h;
      const drift = Math.sin(t * 0.2 + seed * 0.01) * 3;
      const twinkle = Math.sin(t * 0.9 + seed * 0.4);
      const alpha = Math.max(0, 0.06 + twinkle * 0.08);
      const size = 0.3 + (seed % 1.2);

      ctx.save();
      ctx.globalAlpha = alpha;
      ctx.fillStyle = i % 3 === 0 ? "rgba(200, 180, 255, 1)" : "rgba(220, 220, 255, 1)";
      ctx.beginPath();
      ctx.arc(sx, sy + drift, size, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }

    animRef.current = requestAnimationFrame(draw);
  }, []);

  useEffect(() => {
    animRef.current = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(animRef.current);
  }, [draw]);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 w-full h-full pointer-events-none"
      style={{ willChange: "transform" }}
    />
  );
}

/** Compatibility no-op - kept so existing imports don't break */
export function EnergyGlow() {
  return null;
}

/** Vignette overlay for depth - darker edges for focus */
export function Vignette() {
  return (
    <div
      className="absolute inset-0 pointer-events-none z-[1]"
      style={{
        background: "radial-gradient(ellipse 75% 55% at 50% 55%, transparent 30%, hsl(228 20% 4% / 0.7) 100%)",
      }}
    />
  );
}
